// @ts-nocheck
import React, { useState } from 'react';
import { City } from './types';
import FormField from './form-field';

interface CitySelectorProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string, city: City) => void;
  selectedCity: City | null;
  isSearching: boolean;
  onSearch: (value: string) => void;
  searchResults: City[];
  clearResults: () => void;
  labelStyle?: React.CSSProperties;
  onAddressDetailsChange?: (city: City) => void;
  onClear?: () => void;
  required?: boolean;
  placeholder?: string; // New prop for translated placeholder
  searchingText?: string; // New prop for translated searching text
  clearAriaLabel?: string; // New prop for translated clear aria label
  isNational?: boolean; // New prop to indicate if national tab is selected
}

const CitySelector: React.FC<CitySelectorProps> = ({ 
  id, 
  label, 
  value, 
  onChange, 
  selectedCity,
  isSearching,
  onSearch,
  searchResults,
  clearResults,
  labelStyle,
  onAddressDetailsChange,
  onClear,
  required = false,
  placeholder = "Ort oder PLZ eingeben", // Default German text
  searchingText = "Suche...", // Default German text
  clearAriaLabel = "Löschen", // Default German text
  isNational = false // Default to false (show country)
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Update dropdown visibility when search results change
  React.useEffect(() => {
    if (searchResults.length > 0 || isSearching) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
    }
  }, [searchResults, isSearching]);

  // Helper function to format city display (text only)
  const formatCityDisplay = (city: City): string => {
    // Don't show country name for Switzerland when in national mode
    if (isNational && (city.country === 'Switzerland' || city.countryCode === 'CH' || city.countryCode3 === 'CHE')) {
      return `${city.postalCode} ${city.city}`;
    }
    return `${city.postalCode} ${city.city}, ${city.country}`;
  };


  // Input wrapper styles
  const inputWrapperStyle = {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  };

  // Icon button style
  const iconButtonStyle = {
    position: 'absolute' as const,
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '4px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#6b7280',
    transition: 'color 0.2s',
  };

  return (
    <div className="relative">
      <FormField
        id={id}
        label={label}
        labelStyle={labelStyle}
        required={required}
      >
        <div style={inputWrapperStyle}>
          {/* Show flag if city is selected */}
          {selectedCity?.countryCode && value && (
            <img 
               src={`https://flagcdn.com/w20/${selectedCity.countryCode.toLowerCase()}.png`}
              srcSet={`https://flagcdn.com/w40/${selectedCity.countryCode.toLowerCase()}.png 2x`}
              width="20"
              height="15"
              alt={`${selectedCity.country} flag`}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 z-10"
              onError={(e) => {
                // Hide image if it fails to load
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <input
            type="text"
            id={id}
            role="combobox"
            aria-expanded={searchResults.length > 0}
            aria-autocomplete="list"
            className={`w-full p-2 pr-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent ${selectedCity?.countryCode && value ? 'pl-10' : ''}`}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onSearch(e.target.value)}
            onFocus={() => {
              setIsFocused(true);
              // Show dropdown if we have results
              if (searchResults.length > 0) {
                setIsDropdownOpen(true);
              }
            }}
            onBlur={(e) => {
              // Check if the blur is moving to a dropdown item
              const relatedTarget = e.relatedTarget as HTMLElement;
              if (relatedTarget && relatedTarget.getAttribute('role') === 'option') {
                // Don't close dropdown if clicking on an option
                return;
              }
              
              // Delay to allow clicks on dropdown items
              setTimeout(() => {
                setIsFocused(false);
                // Only close if there are no results
                if (searchResults.length === 0) {
                  setIsDropdownOpen(false);
                }
              }, 200);
            }}
          />
          
          {/* Show clear button when there's a value, otherwise show search icon */}
          {value ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onClear) onClear();
              }}
              style={iconButtonStyle}
              className="hover:text-gray-900"
              aria-label={clearAriaLabel}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" fill="currentColor"/>
              </svg>
            </button>
          ) : (
            <div style={iconButtonStyle}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" fill="currentColor"/>
              </svg>
            </div>
          )}
        </div>
        
        {/* Search Results Dropdown */}
        {isDropdownOpen && (
          <div 
            className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
            onMouseDown={(e) => {
              // Prevent blur when clicking inside dropdown
              e.preventDefault();
            }}>
            {isSearching ? (
              <div className="p-4 text-center text-gray-500">
                <svg className="animate-spin h-5 w-5 mx-auto text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="ml-2">{searchingText}</span>
              </div>
            ) : searchResults.length > 0 ? (
              <ul role="listbox">
                {searchResults.map((city, index) => (
                  <li
                    key={`${city.id}-${index}`}
                    role="option"
                    className="p-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                    onClick={() => {
                      onChange(formatCityDisplay(city), city);
                      setIsDropdownOpen(false);
                      clearResults();
                    }}
                  >
                    <div className="flex items-center gap-2">
                      {city.countryCode && (
                        <img 
                          src={`https://flagcdn.com/w20/${city.countryCode.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${city.countryCode.toLowerCase()}.png 2x`}
                          width="20"
                          height="15"
                          alt={`${city.country} flag`}
                          className="flex-shrink-0"
                          onError={(e) => {
                            // Hide image if it fails to load
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      <div>
                        <div className="font-medium">{formatCityDisplay(city)}</div>
                        {city.state && (
                          <div className="text-sm text-gray-600">{city.state}</div>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        )}
      </FormField>
    </div>
  );
};

export default CitySelector;